import React, { useState, useEffect, useMemo } from "react";
import { Modal, Table, Button } from "react-bootstrap";
import {
  columnsMap,
  dates,
  datetimes,
  numericals,
  tables,
  tableNames,
} from "../Models/tablesAndColumns";
import { fetchDistinctNames } from "../Models/distinctQuery";
import { SingleSymbolSearch, MultiSymbolSearch } from "./SymbolSearch";
import { Checkbox } from "@mui/material";
import ProfileSearch from "./ProfileSearch";

const EditModal = ({
  id,
  show,
  handleClose,
  tableTitle,
  selectedSymbol,
  handleFileChange,
  handleInputChange,
  handleEditRow,
  handleMultiInputChange,
  foreignID,
}) => {
  // Memoize the columns to avoid unnecessary recalculations
  const columns = useMemo(() => columnsMap[tableTitle] || [], [tableTitle]);

  const [options, setOptions] = useState({});

  useEffect(() => {
    const fetchOptions = async () => {
      const newOptions = {};

      for (const column of columns) {
        if (tables.includes(column)) {
          const data = await fetchDistinctNames(column);
          if (data?.data) {
            newOptions[column] = data.data;
          }
        }
      }

      setOptions(newOptions);
    };

    fetchOptions();
  }, [columns]); // Fetch options whenever columns change

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          Edit Row {id ? id : ""} in {tableTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Column</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Row ID</td>
              <td>
                <input
                  type="number"
                  value={id ? id : ""}
                  onChange={(e) => handleInputChange("id", e.target.value)}
                  disabled={id}
                />
              </td>
            </tr>
            {columns.map((column, index) => (
              <tr key={index}>
                <td>{column}</td>
                <td>
                  {column === "icon" ? (
                    <input
                      type="file"
                      accept="image/jpeg, image/png"
                      onChange={(e) => handleFileChange(e)}
                    />
                  ) : datetimes.includes(column) &&
                    [
                      "fe_updates",
                      "event",
                      "calendar_event",
                      "stock_recent",
                    ].includes(tableTitle) ? (
                    <input
                      type="datetime-local"
                      onChange={(e) =>
                        handleInputChange(column, e.target.value)
                      }
                    />
                  ) : dates.includes(column) ? (
                    <input
                      type="date"
                      onChange={(e) =>
                        handleInputChange(column, e.target.value)
                      }
                    />
                  ) : column === "name" && tableTitle === "symbol" ? (
                    <input
                      type="text"
                      placeholder="Ticker e.g., AAPL, UBA, TSLA, etc."
                      onChange={(e) =>
                        handleInputChange(column, e.target.value)
                      }
                    />
                  ) : column === "news" && foreignID ? (
                    <input type="number" value={foreignID} disabled={true} />
                  ) : column === "sentiment" ? (
                    <select
                      className="form-control"
                      onChange={(e) =>
                        handleInputChange(column, e.target.value)
                      }
                    >
                      <option value="">Select the Sentiment</option>
                      <option value="positive">Positive</option>
                      <option value="negative">Negative</option>
                      <option value="neutral">Neutral</option>
                    </select>
                  ) : column === "period" && tableTitle === "financials" ? (
                    <select
                      className="form-control"
                      onChange={(e) =>
                        handleInputChange(column, e.target.value)
                      }
                    >
                      <option value="">Select an option</option>
                      <option value="FY">Full Year</option>
                      <option value="TTM">Trailing Twelve Months</option>
                    </select>
                  ) : numericals.includes(column) ? (
                    <input
                      type="number"
                      onChange={(e) =>
                        handleInputChange(column, e.target.value)
                      }
                    />
                  ) : column === "symbol" ? (
                    <SingleSymbolSearch
                      column={column}
                      handleInputChange={handleInputChange}
                    />
                  ) : column === "symbols" ? (
                    <MultiSymbolSearch
                      column={column}
                      handleInputChange={handleMultiInputChange}
                    />
                  ) : tables.includes(column) ? (
                    <select
                      className="form-control"
                      onChange={(e) =>
                        handleInputChange(column, e.target.value)
                      }
                    >
                      <option value="">Select {tableNames[column]}</option>
                      {options[column]?.map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.name}
                        </option>
                      ))}
                    </select>
                  ) : column === "description" ||
                    column === "content" ||
                    column === "analysis" ? (
                    <textarea
                      style={{ width: "100%" }}
                      type="text"
                      placeholder="Input description or content"
                      onChange={(e) =>
                        handleInputChange(column, e.target.value)
                      }
                    />
                  ) : column === "reason" ? (
                    <textarea
                      style={{ width: "100%" }}
                      type="text"
                      placeholder="State the reason"
                      onChange={(e) =>
                        handleInputChange(column, e.target.value)
                      }
                    />
                  ) : column === "priority" ? (
                    <select
                      className="form-control"
                      onChange={(e) =>
                        handleInputChange(column, e.target.value)
                      }
                    >
                      <option value="">Select the Priority</option>
                      <option value="0">Zero Priority</option>
                      <option value="1">One Priority</option>
                      <option value="2">Two Priority</option>
                    </select>
                  ) : column === "duration" ? (
                    <select
                      className="form-control"
                      onChange={(e) =>
                        handleInputChange(column, e.target.value)
                      }
                    >
                      <option value="">Select the Interval</option>
                      <option value="monthly">Monthly</option>
                      <option value="annually">Annually</option>
                      <option value="Forever">Forever</option>
                    </select>
                  ) : column === "disabled" ||
                    column === "live" ||
                    column === "status" ||
                    column === "internal" ||
                    (column === "email" &&
                      (tableTitle === "notification_profile" ||
                        tableTitle === "notification_country" ||
                        tableTitle === "notification_investor_type" ||
                        tableTitle === "notification_asset" ||
                        tableTitle === "notification_industry")) ? (
                    <Checkbox
                      //checked={value === 1}
                      onChange={(e) =>
                        handleInputChange(column, e.target.checked ? 1 : 0)
                      }
                    />
                  ) : column === "profile" ||
                    (column === "author" && tableTitle === "blog") ? (
                    <ProfileSearch
                      column={column}
                      handleInputChange={handleInputChange}
                    />
                  ) : (
                    <input
                      type="text"
                      onChange={(e) =>
                        handleInputChange(column, e.target.value)
                      }
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Button variant="dark" className="mt-3" onClick={handleEditRow}>
          Edit Row
        </Button>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditModal;
